import type React from 'react';
import { useState, useEffect } from 'react';
import { ShowModal } from '../Modal';
import { ProductReviewModal } from '../ProductReviewModal';
import type { StatusDeliveryStep } from 'src/model/StatusDeliveryTypes';
import { StatusTracker } from '../StatusTracker';
import { api } from 'src/services/api';
import { Card, CardContent } from 'src/components/ui/card';
import {
  Package,
  Star,
  MessageCircle,
  RefreshCw,
  Calendar,
  Truck,
  ChevronRight,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import AutocompleteBank from 'src/components/AutoCompleteBank/AutoCompleteBank';

interface OrderItem {
  id: string;
  itemName: string;
  unitValue: number;
  totalValue: number;
  imageUrl: string;
  variation: string;
  sellerId: string;
  productId?: string; // Adicionar productId para avaliação
}

interface Order {
  id: string;
  productName: string;
  variation: string;
  price: string;
  items: OrderItem[];
  status:
    | 'PENDING_PAYMENT'
    | 'A Pagar'
    | 'Preparando'
    | 'A Caminho'
    | 'Finalizado'
    | 'Cancelado'
    | 'Devolução/Reembolso';
  COMPLETE: boolean;
  imageUrl: string;
  orderDate: string;
  totalValue: number;
  paymentStatus: string;
  paymentMethod?: string;
  pagarmeStatus?: 'paid' | 'pending' | 'checked';
  iniPeriodRefund?: string;
  endPeriodRefund?: string;
}

interface Store {
  id: string;
  name: string;
  orders: Order[];
}

interface Tab {
  title: string;
  key: string;
  count?: number;
}

const REFUND_WINDOW_DAYS = 30;

type RefundReason =
  | 'produto_nao_entregue'
  | 'produto_atraso'
  | 'diff_anunciado'
  | 'defeito'
  | 'nao_funciona'
  | 'incompleto'
  | 'aparencia_dif'
  | 'cancelado_seller'
  | 'cobranca_dup'
  | 'fraude'
  | 'arrependimento'
  | 'garantia_nao_cumprida'
  | 'qtd_errada'
  | 'perigoso'
  | 'assinatura'
  | 'imagem_enganosa'
  | 'falha_ativacao'
  | 'endereco_errado'
  | 'usado_como_novo'
  | 'conteudo_def';

const FRONT_TO_BACK_MOTIVE: Record<RefundReason, string> = {
  produto_nao_entregue: 'PRODUTO_NAO_ENTREGUE',
  produto_atraso: 'PRODUTO_ENTREGUE_COM_ATRASO',
  diff_anunciado: 'PRODUTO_DIFERENTE_DO_ANUNCIADO',
  defeito: 'PRODUTO_COM_DEFEITO_OU_AVARIADO',
  nao_funciona: 'PRODUTO_NAO_FUNCIONA_COMO_ESPERADO',
  incompleto: 'PRODUTO_INCOMPLETO',
  aparencia_dif: 'PRODUTO_COM_APARENCIA_DIFERENTE',
  cancelado_seller: 'PEDIDO_CANCELADO_PELO_VENDEDOR',
  cobranca_dup: 'COBRANCA_INDEVIDA_OU_DUPLICADA',
  fraude: 'FRAUDE_TRANSACAO_NAO_RECONHECIDA',
  arrependimento: 'ARREPENDIMENTO_DA_COMPRA',
  garantia_nao_cumprida: 'PRODUTO_COM_GARANTIA_NAO_CUMPRIDA',
  qtd_errada: 'QUANTIDADE_ERRADA_RECEBIDA',
  perigoso: 'PRODUTO_PERIGOSO_OU_NAO_CONFORME',
  assinatura: 'PROBLEMAS_COM_ASSINATURA_OU_COBRANCA_RECORRENTE',
  imagem_enganosa: 'IMAGEM_OU_DESCRICAO_ENGANOSA',
  falha_ativacao: 'FALHA_NA_ATIVACAO_OU_CODIGO_INVALIDO',
  endereco_errado: 'ENTREGA_EM_ENDERECO_ERRADO',
  usado_como_novo: 'PRODUTO_USADO_ENVIADO_COMO_NOVO',
  conteudo_def: 'CONTEUDO_DIGITAL_NAO_ENTREGUE_OU_COM_DEFEITO',
};

const REFUND_REASONS: { key: RefundReason; label: string }[] = [
  { key: 'produto_nao_entregue', label: 'Produto não entregue' },
  { key: 'produto_atraso', label: 'Produto entregue com atraso' },
  { key: 'diff_anunciado', label: 'Produto diferente do anunciado' },
  { key: 'defeito', label: 'Produto com defeito / avariado' },
  { key: 'nao_funciona', label: 'Produto não funciona como esperado' },
  {
    key: 'incompleto',
    label: 'Produto incompleto (peças ou acessórios faltando)',
  },
  {
    key: 'aparencia_dif',
    label: 'Produto com aparência diferente (cor/tamanho/material divergente)',
  },
  { key: 'cancelado_seller', label: 'Pedido cancelado pelo vendedor' },
  { key: 'cobranca_dup', label: 'Cobrança indevida / duplicada' },
  { key: 'fraude', label: 'Fraude (transação não reconhecida)' },
  { key: 'arrependimento', label: 'Arrependimento da compra' },
  { key: 'garantia_nao_cumprida', label: 'Produto com garantia não cumprida' },
  { key: 'qtd_errada', label: 'Quantidade errada recebida' },
  { key: 'perigoso', label: 'Produto perigoso / não conforme normas' },
  {
    key: 'assinatura',
    label: 'Problemas com assinatura / cobrança recorrente',
  },
  { key: 'imagem_enganosa', label: 'Imagem ou descrição enganosa' },
  { key: 'falha_ativacao', label: 'Falha na ativação / código inválido' },
  { key: 'endereco_errado', label: 'Entrega em endereço errado' },
  { key: 'usado_como_novo', label: 'Produto usado enviado como novo' },
  {
    key: 'conteudo_def',
    label: 'Conteúdo digital não entregue ou com defeito',
  },
];

const REASONS_REQUIRE_IMAGES = new Set<RefundReason>([
  'diff_anunciado',
  'defeito',
  'nao_funciona',
  'incompleto',
  'aparencia_dif',
  'garantia_nao_cumprida',
  'qtd_errada',
  'perigoso',
  'imagem_enganosa',
  'usado_como_novo',
  'conteudo_def',
]);

const regexDescricao = /^[0-9A-Za-zÀ-ÿ\s.\\,-]{1,1000}$/;

const parseTs = (v: unknown) => {
  const t = Date.parse(String(v ?? ''));
  return Number.isFinite(t) ? t : NaN;
};

const isWithinRefundWindow = (order: Order) => {
  if (!order.iniPeriodRefund || !order.endPeriodRefund) return false;

  const iniTs = new Date(order.iniPeriodRefund).getTime();
  const endTs = new Date(order.endPeriodRefund).getTime();

  if (Number.isNaN(iniTs) || Number.isNaN(endTs)) return false;

  const now = Date.now();
  return now >= iniTs && now <= endTs;
};

const tabs: Tab[] = [
  { title: 'Tudo', key: 'all' },
  { title: 'A Pagar', key: 'A Pagar' },
  { title: 'Preparando', key: 'Preparando' },
  { title: 'A Caminho', key: 'A Caminho' },
  { title: 'Finalizado', key: 'Finalizado' },
  { title: 'Cancelado', key: 'Cancelado' },
  { title: 'Devolução/Reembolso', key: 'Devolução/Reembolso' },
];

const mapOrderStatus = (
  paymentStatus: string | undefined,
  orderStatus: string | undefined
): Order['status'] => {
  const statusMap: Record<string, Order['status']> = {
    PENDING_PAYMENT: 'A Pagar',
    PREPARING: 'Preparando',
    ON_THE_WAY: 'A Caminho',
    COMPLETED: 'Finalizado',
    CANCELED: 'Cancelado',
    REFUND: 'Devolução/Reembolso',
    RETURNED: 'Devolução/Reembolso',
  };

  return statusMap[orderStatus ?? ''] || 'Preparando';
};

const getStatusColor = (status: Order['status']) => {
  const colorMap: Record<Order['status'], string> = {
    PENDING_PAYMENT: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'A Pagar': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    Preparando: 'bg-blue-100 text-blue-800 border-blue-200',
    'A Caminho': 'bg-purple-100 text-purple-800 border-purple-200',
    Finalizado: 'bg-green-100 text-green-800 border-green-200',
    Cancelado: 'bg-red-100 text-red-800 border-red-200',
    'Devolução/Reembolso': 'bg-orange-100 text-orange-800 border-orange-200',
  };
  return colorMap[status];
};

const getStatusIcon = (status: Order['status']) => {
  const iconMap: Record<Order['status'], React.ReactNode> = {
    PENDING_PAYMENT: <Calendar className="w-3 h-3" />,
    'A Pagar': <Calendar className="w-3 h-3" />,
    Preparando: <Package className="w-3 h-3" />,
    'A Caminho': <Truck className="w-3 h-3" />,
    Finalizado: <Package className="w-3 h-3" />,
    Cancelado: <RefreshCw className="w-3 h-3" />,
    'Devolução/Reembolso': <RefreshCw className="w-3 h-3" />,
  };
  return iconMap[status];
};

export const OrderTracking: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('all');
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [trackingSteps, setTrackingSteps] = useState<StatusDeliveryStep[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [isBankDataModalOpen, setIsBankDataModalOpen] = useState(false);
  const [bankData, setBankData] = useState({
    bank: '',
    branch_number: '',
    branch_check_digit: '',
    account_number: '',
    account_check_digit: '',
    type: 'corrente',
  });
  const [selectedOrderForCancel, setSelectedOrderForCancel] =
    useState<Order | null>(null);

  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [refundTarget, setRefundTarget] = useState<{
    order: Order;
    sellerId: string;
  } | null>(null);
  const [refundReason, setRefundReason] = useState<RefundReason | null>(null);
  const [refundDescription, setRefundDescription] = useState('');
  const [refundFiles, setRefundFiles] = useState<File[]>([]);
  const [refundSubmitting, setRefundSubmitting] = useState(false);

  // Estados para o modal de avaliação
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedProductForReview, setSelectedProductForReview] = useState<{
    productId: string;
    productName: string;
    productImage: string;
  } | null>(null);

  const history = useHistory();

  const fileToBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => reject(new Error('fail_read'));
      r.readAsDataURL(file);
    });

  const isPending = (s: Order['status']) =>
    s === 'PENDING_PAYMENT' || s === 'A Pagar';

  const canCancel = (o: Order) =>
    o.status === 'A Pagar' || o.status === 'Preparando';

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await api.get('/order/allbyclient/');
        const data = Array.isArray(response.data) ? response.data : [];

        const formattedOrders: Order[] = data.map((order: any) => {
          const items: OrderItem[] = (order.orderItems ?? []).map(
            (item: any, idx: number) => ({
              id: item?.id ? `${item.id}-${idx}` : `item-${order.id}-${idx}`,
              itemName: item?.itemName ?? 'Produto',
              unitValue: Number(item?.unitValue ?? 0),
              totalValue: Number(item?.totalValue ?? 0),
              imageUrl: item.grade?.image
                ? `${process.env.REACT_APP_BASE_URL}/uploads/${item.grade.image}`
                : item.productDTO?.imagens?.[0] || '',
              variation:
                item?.gradeSize?.size ??
                item?.productDTO?.grade?.[0]?.gradeSizes?.[0]?.size ??
                'Único',
              sellerId: item?.productDTO?.storeSeller?.id ?? '',
              productId: item?.productDTO?.id ?? '',
              iniPeriodRefund: order.iniPeriodRefund ?? null,
              endPeriodRefund: order.endPeriodRefund ?? null,
            })
          );

          const firstItem = items[0];

          return {
            id: String(order.id),
            productName: `Pedido #${String(order.id).slice(-8)}`,
            variation: firstItem?.variation ?? 'Único',
            price: firstItem
              ? `R$ ${firstItem.unitValue.toFixed(2)}`
              : 'R$ 0,00',
            items,
            status: mapOrderStatus(order.paymentStatus, order.orderStatus),
            COMPLETE: order.orderStatus === 'COMPLETE',
            imageUrl: '',
            orderDate: order.orderDate,
            totalValue: Number(order.totalValue ?? 0),
            paymentStatus: order.paymentStatus ?? '',
            paymentMethod: order.paymentMethod ?? '',
            pagarmeStatus: order.pagarmeStatus ?? undefined,
            iniPeriodRefund: order.iniPeriodRefund ?? undefined,
            endPeriodRefund: order.endPeriodRefund ?? undefined,
          };
        });

        formattedOrders.sort(
          (a, b) =>
            new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime()
        );

        const storesMap = new Map<string, Store>();

        data.forEach((raw: any) => {
          const orderId = String(raw.id);
          const orderRef = formattedOrders.find((o) => o.id === orderId);
          if (!orderRef) return;

          const firstSeller =
            raw?.orderItems?.[0]?.productDTO?.storeSeller?.name ??
            'Loja Desconhecida';
          const storeId = `store-${firstSeller}`;

          if (!storesMap.has(storeId)) {
            storesMap.set(storeId, {
              id: storeId,
              name: firstSeller,
              orders: [],
            });
          }
          storesMap.get(storeId)!.orders.push(orderRef);
        });

        const toTs = (v: unknown) => {
          const t = Date.parse(String(v ?? ''));
          return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
        };

        const storesArr = Array.from(storesMap.values()).map((s) => ({
          ...s,
          orders: [...s.orders].sort(
            (a, b) => toTs(b.orderDate) - toTs(a.orderDate)
          ),
        }));

        storesArr.sort(
          (a, b) => toTs(b.orders[0]?.orderDate) - toTs(a.orders[0]?.orderDate)
        );

        setOrders(
          formattedOrders.sort(
            (a, b) => Date.parse(b.orderDate) - Date.parse(a.orderDate)
          )
        );
        setStores(storesArr);
      } catch (error) {
        console.error('Erro ao buscar os pedidos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const handleChat = async (sellerId: string): Promise<void> => {
    console.log(sellerId);
    if (!sellerId) return;
    try {
      const response = await api.post('/chat', sellerId);

      toast.success('Chat criado com sucesso!');
      history.push('/user-profile?tab=chat');
    } catch (error: any) {
      if (error.response?.status === 400) {
        toast.info('Você já possui um chat ativo com esta loja.');
      } else {
        console.error('Erro ao criar o chat:', error);
        toast.error('Erro ao iniciar o chat. Tente novamente.');
      }
    }
  };

  const handleRefundDescriptionChange = (v: string) => {
    const sanitized = v.normalize('NFKC').replace(/[^\dA-Za-zÀ-ÿ\s.,-]/g, '');
    setRefundDescription(sanitized.slice(0, 1000));
  };

  const handleRefundFilesChange = (files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files).slice(0, 3);
    const valid = arr.filter((f) => {
      const okType = ['image/jpeg', 'image/png'].includes(f.type);
      const okName = /\.(jpe?g|png)$/i.test(f.name);
      const okSize = f.size <= 8 * 1024 * 1024;
      return okType && okName && okSize;
    });
    setRefundFiles(valid.slice(0, 3));
  };

  const validateRefundForm = () => {
    if (!refundTarget) return 'Pedido inválido.';
    if (!refundTarget.sellerId) return 'Vendedor inválido.';
    if (!refundReason) return 'Selecione um motivo.';
    if (!regexDescricao.test(refundDescription)) return 'Descrição inválida.';
    if (REASONS_REQUIRE_IMAGES.has(refundReason) && refundFiles.length === 0)
      return 'Imagens obrigatórias para o motivo selecionado.';
    return null;
  };

  const submitRefund = async () => {
    const err = validateRefundForm();
    if (err) {
      toast.info(err);
      return;
    }
    if (!refundTarget) {
      toast.info('Pedido inválido.');
      return;
    }
    if (!isWithinRefundWindow(refundTarget.order)) {
      toast.info('Pedido fora do prazo de reembolso.');
      return;
    }

    try {
      setRefundSubmitting(true);

      const imagesB64 = await Promise.all(refundFiles.map(fileToBase64));

      const payload = {
        id: refundTarget.order.id,
        motive: FRONT_TO_BACK_MOTIVE[refundReason!],
        description: refundDescription,
        sellers: [{ id: refundTarget.sellerId }],
        images: imagesB64,
      };

      await api.post('/order/request-repayment-order', payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      setIsRefundModalOpen(false);
      toast.success('Solicitação de reembolso enviada.');
    } catch (e: any) {
      if (e?.response?.status === 422) {
        toast.error(e.response?.data?.mensagem ?? 'Falha de validação.');
      } else {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Falha ao enviar solicitação.';
        toast.error(msg);
      }
    } finally {
      setRefundSubmitting(false);
    }
  };

  const filteredStores = stores.map((store) => ({
    ...store,
    orders: store.orders.filter((order) => {
      if (activeTab === 'all') return true;
      return order.status === activeTab;
    }),
  }));

  const hasOrders = filteredStores.some((store) => store.orders.length > 0);

  const openStatusModal = (steps: StatusDeliveryStep[]) => {
    setTrackingSteps(steps);
    setIsStatusModalOpen(true);
  };

  const shouldShowCancel = (o: Order) =>
    canCancel(o) || o.status === 'A Caminho';

  const openCancelModal = (orderId: string) => {
    const order = orders.find((o) => o.id === orderId);
    if (!order) return;

    if (order.status === 'A Caminho') {
      toast.info('O status do pedido não permite cancelamento');
      return;
    }
    if (!canCancel(order)) return;

    setSelectedOrderId(orderId);
    setSelectedOrderForCancel(order);

    const shouldAskBank =
      order.paymentMethod === 'BOLETO' && order.pagarmeStatus === 'paid';

    if (shouldAskBank) {
      setIsBankDataModalOpen(true);
    } else {
      setIsCancelModalOpen(true);
    }
  };

  const closeCancelModal = () => {
    setIsCancelModalOpen(false);
    setSelectedOrderId(null);
    setSelectedOrderForCancel(null);
  };

  const closeBankDataModal = () => {
    setIsBankDataModalOpen(false);
    setSelectedOrderId(null);
    setSelectedOrderForCancel(null);
    setBankData({
      bank: '',
      branch_number: '',
      branch_check_digit: '',
      account_number: '',
      account_check_digit: '',
      type: 'corrente',
    });
  };

  const handleBankDataChange = (field: string, value: string) => {
    setBankData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancelOrder = async () => {
    if (!selectedOrderId || !selectedOrderForCancel) return;

    try {
      setCancelLoading(true);

      const shouldSendBank =
        selectedOrderForCancel.paymentMethod === 'BOLETO' &&
        selectedOrderForCancel.pagarmeStatus === 'paid';

      const payload: any = {
        id: selectedOrderId,
        paymentMethod: selectedOrderForCancel.paymentMethod,
        ...(shouldSendBank && {
          bank: {
            bank: bankData.bank,
            branch_number: bankData.branch_number,
            branch_check_digit: bankData.branch_check_digit,
            account_number: bankData.account_number,
            account_check_digit: bankData.account_check_digit,
            type: bankData.type,
          },
        }),
      };

      await api.post('/order/cancel-order', payload);

      setOrders((prev) =>
        prev.map((o) =>
          o.id === selectedOrderId ? { ...o, status: 'Cancelado' } : o
        )
      );
      setStores((prev) =>
        prev.map((s) => ({
          ...s,
          orders: s.orders.map((o) =>
            o.id === selectedOrderId ? { ...o, status: 'Cancelado' } : o
          ),
        }))
      );

      closeCancelModal();
      closeBankDataModal();
      toast.success('Pedido cancelado com sucesso!');
    } catch (e: any) {
      if (e.response?.status === 422) {
        toast.error(e.response.data?.mensagem);
        return;
      }
      const msg =
        e?.response?.data?.message ||
        'Erro ao cancelar pedido. Tente novamente.';
      toast.error(msg);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleOpenReviewModal = (item: OrderItem) => {
    if (!item.productId) {
      toast.error('ID do produto não encontrado');
      return;
    }

    setSelectedProductForReview({
      productId: item.productId,
      productName: item.itemName,
      productImage: item.imageUrl,
    });
    setIsReviewModalOpen(true);
  };

  const handleReviewSubmitted = () => {
    // Aqui você pode atualizar a lista de pedidos se necessário
    // ou apenas fechar o modal
    toast.success('Obrigado pela sua avaliação!');
  };

  const tabsWithCount = tabs.map((tab) => {
    if (tab.key === 'all') {
      return { ...tab, count: orders.length };
    }
    const count = orders.filter((order) => order.status === tab.key).length;
    return { ...tab, count };
  });

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-gray-50">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
          <span className="ml-2 text-gray-600">Carregando pedidos...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-2 py-4">
          <h1 className="text-xl font-bold text-gray-900 mb-1">Meus Pedidos</h1>
          <p className="text-sm text-gray-600">
            Acompanhe o status dos seus pedidos
          </p>
        </div>

        <div className="px-2 pb-2">
          <div className="flex space-x-1 overflow-x-auto scrollbar-hide">
            {tabsWithCount.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-shrink-0 pb-3 px-3 text-sm font-medium whitespace-nowrap transition-colors duration-200 border-b-2 ${
                  activeTab === tab.key
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.title}
                {tab.count !== undefined && tab.count > 0 && (
                  <span
                    className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                      activeTab === tab.key
                        ? 'bg-red-100 text-red-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-2 py-4 space-y-4">
        {hasOrders ? (
          filteredStores.map((store) =>
            store.orders.length > 0 ? (
              <div key={store.id} className="space-y-3">
                <div className="bg-white rounded-lg px-2 py-3 shadow-sm">
                  <h2 className="font-semibold text-base text-gray-900">
                    {store.name}
                  </h2>
                </div>

                {store.orders.map((order) => (
                  <Card key={order.id} className="shadow-sm">
                    <CardContent className="p-0">
                      <div className="p-4 border-b border-gray-100">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">
                              {order.productName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {new Date(order.orderDate).toLocaleDateString(
                                'pt-BR'
                              )}
                            </p>
                          </div>
                          <Badge
                            variant="outline"
                            className={`${getStatusColor(order.status)} flex items-center gap-1 text-xs`}
                          >
                            {getStatusIcon(order.status)}
                            {order.status}
                          </Badge>
                        </div>
                      </div>

                      <div className="p-4 space-y-4">
                        {order.items.map((item) => (
                          <div
                            key={item.id}
                            className="flex gap-4 p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
                          >
                            <div className="w-20 h-20 bg-gray-50 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0 border border-gray-100">
                              <img
                                src={item.imageUrl || '/placeholder.svg'}
                                alt={item.itemName}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  target.parentElement!.innerHTML =
                                    '<Package class="w-8 h-8 text-gray-400" />';
                                }}
                              />
                            </div>

                            <div className="flex-1 min-w-0 flex flex-col justify-between">
                              <div className="space-y-1">
                                <h3 className="font-semibold text-gray-900 text-base leading-tight line-clamp-2">
                                  {item.itemName}
                                </h3>
                                {item.variation && (
                                  <p className="text-sm text-gray-500">
                                    {item.variation}
                                  </p>
                                )}
                              </div>

                              <div className="flex items-center justify-between mt-3 gap-3">
                                <div className="flex flex-col">
                                  <span className="text-lg font-bold text-gray-900 max-sm:text-base">
                                    R${' '}
                                    {item.unitValue
                                      .toFixed(2)
                                      .replace('.', ',')}
                                  </span>
                                </div>

                                {order.status === 'Finalizado' ? (
                                  isWithinRefundWindow(order) ? (
                                    <div className="flex gap-2">
                                      <Button
                                        onClick={() =>
                                          handleOpenReviewModal(item)
                                        }
                                        variant="outline"
                                        size="sm"
                                        className="text-yellow-600 border-yellow-200 hover:text-yellow-700 hover:bg-yellow-50 hover:border-yellow-300 px-3 py-2 h-auto whitespace-nowrap 
                                        max-sm:text-xs max-sm:px-2 max-sm:py-1 max-sm:gap-1"
                                      >
                                        <Star className="w-4 h-4 mr-2" />
                                        Avaliar
                                      </Button>

                                      <Button
                                        onClick={() => {
                                          setRefundTarget({
                                            order,
                                            sellerId: item.sellerId,
                                          });
                                          setRefundReason(null);
                                          setRefundDescription('');
                                          setRefundFiles([]);
                                          setIsRefundModalOpen(true);
                                        }}
                                        variant="outline"
                                        size="sm"
                                        className="text-red-600 border-red-200 hover:text-red-700 hover:bg-red-50 hover:border-red-300 px-3 py-2 h-auto whitespace-nowrap 
                                        max-sm:text-xs max-sm:px-2 max-sm:py-1 max-sm:gap-1"
                                      >
                                        <RefreshCw className="w-4 h-4 mr-2" />
                                        Pedir Reembolso
                                      </Button>
                                    </div>
                                  ) : (
                                    <Button
                                      onClick={() =>
                                        handleOpenReviewModal(item)
                                      }
                                      variant="outline"
                                      size="sm"
                                      className="text-yellow-600 border-yellow-200 hover:text-yellow-700 hover:bg-yellow-50 hover:border-yellow-300 px-3 py-2 h-auto whitespace-nowrap 
                                      max-sm:text-xs max-sm:px-2 max-sm:py-1 max-sm:gap-1"
                                    >
                                      <Star className="w-4 h-4 mr-2" />
                                      Avaliar
                                    </Button>
                                  )
                                ) : (
                                  <Button
                                    onClick={() => handleChat(item.sellerId)}
                                    variant="outline"
                                    size="sm"
                                    className="text-red-600 border-red-200 hover:text-red-700 hover:bg-red-50 hover:border-red-300 px-3 py-2 h-auto whitespace-nowrap 
             max-sm:text-xs max-sm:px-2 max-sm:py-1 max-sm:gap-1"
                                  >
                                    <MessageCircle className="w-4 h-4 mr-2" />
                                    Falar com Vendedor
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}

                        <div className="flex justify-end px-2">
                          <p className="text-sm font-semibold text-gray-900">
                            Total do Pedido:{' '}
                            <span className="text-base text-red-600">
                              R$ {order.totalValue.toFixed(2).replace('.', ',')}
                            </span>
                          </p>
                        </div>
                      </div>

                      <div className="p-4 bg-white border-t border-gray-100">
                        {canCancel(order) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openCancelModal(order.id)}
                            className="w-full border-red-200 text-red-600 hover:bg-red-50 h-9"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Cancelar Pedido
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : null
          )
        ) : (
          <Card className="mx-4">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Package className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Nenhum pedido encontrado
              </h3>
              <p className="text-gray-600 text-center text-sm">
                {activeTab === 'all'
                  ? 'Você ainda não fez nenhum pedido.'
                  : `Nenhum pedido com status "${activeTab}".`}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modal de Avaliação de Produto */}
      {selectedProductForReview && (
        <ProductReviewModal
          visible={isReviewModalOpen}
          onSetVisible={setIsReviewModalOpen}
          productId={selectedProductForReview.productId}
          productName={selectedProductForReview.productName}
          productImage={selectedProductForReview.productImage}
          onReviewSubmitted={handleReviewSubmitted}
        />
      )}

      {/* Modal de Reembolso */}
      <ShowModal
        visible={isRefundModalOpen}
        onSetVisible={setIsRefundModalOpen}
        title="Pedir Reembolso"
        showCloseButton
      >
        <div className="p-4 space-y-4">
          <div className="space-y-2 max-h-56 overflow-auto pr-1">
            {REFUND_REASONS.map((r) => (
              <label key={r.key} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="refund_reason"
                  value={r.key}
                  checked={refundReason === r.key}
                  onChange={() => setRefundReason(r.key)}
                />
                <span>{r.label}</span>
                {REASONS_REQUIRE_IMAGES.has(r.key) && (
                  <span className="text-red-600 text-xs">#</span>
                )}
              </label>
            ))}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              Descrição do problema (até 1000)
            </label>
            <textarea
              value={refundDescription}
              onChange={(e) => handleRefundDescriptionChange(e.target.value)}
              maxLength={1000}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent h-28"
              placeholder="Descreva objetivamente o problema"
            />
            <div className="text-xs text-gray-500">
              {refundDescription.length}/1000
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Imagens
              {refundReason && REASONS_REQUIRE_IMAGES.has(refundReason) && (
                <span className="text-red-600 ml-1">(obrigatórias)</span>
              )}
            </label>
            <input
              type="file"
              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
              multiple
              onChange={(e) => handleRefundFilesChange(e.target.files)}
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
            />
            {refundFiles.length > 0 && (
              <div className="text-xs text-gray-600">
                {refundFiles.length} arquivo(s) selecionado(s), máx. 3
              </div>
            )}
          </div>

          <div className="space-y-3">
            <Button
              onClick={submitRefund}
              disabled={refundSubmitting}
              className="w-full bg-red-600 hover:bg-red-700 text-white h-11"
            >
              {refundSubmitting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar Solicitação'
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsRefundModalOpen(false)}
              disabled={refundSubmitting}
              className="w-full border-red-200 text-red-600 hover:bg-red-50 h-11 bg-transparent"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </ShowModal>

      {/* Modal de Status */}
      <ShowModal
        visible={isStatusModalOpen}
        onSetVisible={setIsStatusModalOpen}
        title="Status do Pedido"
        showCloseButton
      >
        <StatusTracker steps={trackingSteps} />
      </ShowModal>

      {/* Modal de Cancelamento */}
      <ShowModal
        visible={isCancelModalOpen}
        onSetVisible={setIsCancelModalOpen}
        title=""
        showCloseButton
      >
        <div className="p-4">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RefreshCw className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-red-900 mb-2 text-center">
              Confirmar Cancelamento
            </h3>
            <p className="text-red-700/70 text-center text-sm">
              Tem certeza que deseja cancelar este pedido? Esta ação não pode
              ser desfeita.
            </p>
          </div>

          <div className="space-y-3">
            <Button
              onClick={handleCancelOrder}
              disabled={cancelLoading}
              className="w-full bg-red-600 hover:bg-red-700 text-white h-11"
            >
              {cancelLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Cancelando...
                </>
              ) : (
                'Sim, cancelar pedido'
              )}
            </Button>
            <Button
              variant="outline"
              onClick={closeCancelModal}
              disabled={cancelLoading}
              className="w-full border-red-200 text-red-600 hover:bg-red-50 h-11 bg-transparent"
            >
              Não, manter pedido
            </Button>
          </div>
        </div>
      </ShowModal>

      {/* Modal de Dados Bancários */}
      <ShowModal
        visible={isBankDataModalOpen}
        onSetVisible={setIsBankDataModalOpen}
        title="Dados Bancários para Reembolso"
        showCloseButton
      >
        <div className="p-4">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Informe seus dados bancários
            </h3>
            <p className="text-gray-600 text-sm">
              Como seu pedido foi pago via boleto, precisamos dos seus dados
              bancários para processar o reembolso.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <AutocompleteBank
                value={bankData.bank}
                onChange={(val) =>
                  setBankData((prev) => ({
                    ...prev,
                    bank: val,
                  }))
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Agência
                </label>
                <input
                  type="text"
                  value={bankData.branch_number}
                  onChange={(e) =>
                    handleBankDataChange('branch_number', e.target.value)
                  }
                  placeholder="Ex: 8060"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dígito Agência
                </label>
                <input
                  type="text"
                  value={bankData.branch_check_digit}
                  onChange={(e) =>
                    handleBankDataChange('branch_check_digit', e.target.value)
                  }
                  placeholder="Ex: 0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conta
                </label>
                <input
                  type="text"
                  value={bankData.account_number}
                  onChange={(e) =>
                    handleBankDataChange('account_number', e.target.value)
                  }
                  placeholder="Ex: 295569"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dígito Conta
                </label>
                <input
                  type="text"
                  value={bankData.account_check_digit}
                  onChange={(e) =>
                    handleBankDataChange('account_check_digit', e.target.value)
                  }
                  placeholder="Ex: 4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Conta
              </label>
              <select
                value={bankData.type}
                onChange={(e) => handleBankDataChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="corrente">Conta Corrente</option>
                <option value="poupanca">Conta Poupança</option>
              </select>
            </div>
          </div>

          <div className="space-y-3 mt-6">
            <Button
              onClick={handleCancelOrder}
              disabled={
                cancelLoading ||
                !bankData.bank ||
                !bankData.branch_number ||
                !bankData.account_number
              }
              className="w-full bg-red-600 hover:bg-red-700 text-white h-11"
            >
              {cancelLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Cancelando...
                </>
              ) : (
                'Cancelar Pedido e Solicitar Reembolso'
              )}
            </Button>
            <Button
              variant="outline"
              onClick={closeBankDataModal}
              disabled={cancelLoading}
              className="w-full border-red-200 text-red-600 hover:bg-red-50 h-11 bg-transparent"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </ShowModal>
    </div>
  );
};
